import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PhoneNumber } from "@vd/ui";
import { AudioLines } from "lucide-react";
import { useState } from "react";
import { callQuery } from "../../../api/admin";
import { ApiError } from "../../../api/client";
import type { AdminCallDetail, CallTimelineEvent, Destination } from "../../../api/types";
import { Crumbs } from "../../../components/Crumbs";
import { QualityChart } from "../../../components/QualityChart";
import { CoachingNotes } from "../../../components/team/CoachingNotes";
import { Badge, Card, cx, EmptyState, ErrorState, Loading, Segmented } from "../../../components/ui";
import { API_BASE } from "../../../config";
import { useCallName } from "../../../lib/crumbLabels";
import { level } from "../../../lib/crumbs";
import { fmtDateTime, fmtDuration, fmtPhone, fmtTime } from "../../../lib/format";
import { dispositionName, routingBadge } from "../../../lib/labels";
import { recordingState } from "../../../lib/postCall";

export const Route = createFileRoute("/admin/calls/$callId")({
  staticData: {
    crumb: {
      label: "Call",
      useLabel: ({ params }) => useCallName(params.callId ?? ""),
      parents: [level("Calls", "/admin/calls")],
    },
  },
  loader: ({ context, params }) => context.queryClient.prefetchQuery(callQuery(params.callId)),
  component: CallPage,
});

const SOURCE_LABEL: Record<CallTimelineEvent["source"], string> = {
  telnyx: "Telnyx",
  client: "App",
  server: "Server",
};
const LEVEL_DOT: Record<CallTimelineEvent["level"], string> = {
  debug: "bg-ink-3",
  info: "bg-hairline-strong",
  warn: "bg-warning",
  error: "bg-danger",
};

function describeDestination(d: Destination | null): string {
  if (!d) return "Not decided";
  switch (d.kind) {
    case "contact":
      return `Contact ${d.ref ? `#${d.ref.id}` : ""}`.trim();
    case "deal":
      return `Opportunity ${d.ref ? `#${d.ref.id}` : ""}`.trim();
    case "new_deal":
      return `New opportunity${d.new_deal_name ? `: ${d.new_deal_name}` : ""}`;
    default:
      return "Not posted";
  }
}

function CallPage() {
  const { callId } = Route.useParams();
  const q = useQuery(callQuery(callId));
  if (q.isPending) return <Loading />;
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <>
        <BackLink />
        {notFound ? (
          <EmptyState
            title="This call doesn't exist"
            description="It may have been removed by the retention policy."
          />
        ) : (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        )}
      </>
    );
  }
  return <CallView call={q.data} />;
}

/** The page's crumbs (ADR 0030: from the trail and the route's declaration). */
function BackLink(_props: { title?: string }) {
  return <Crumbs className="mb-2" />;
}

function CallView({ call }: { call: AdminCallDetail }) {
  const recording = recordingState(call);
  const other = call.direction === "outbound" ? call.to : call.from;
  const title = call.contact?.name ?? fmtPhone(other);
  const routing = routingBadge(call.routing.status);
  const facts: [string, React.ReactNode][] = [
    ["Started", fmtDateTime(call.started_at)],
    ["Duration", call.duration_s !== undefined ? fmtDuration(call.duration_s) : "In progress"],
    ["Rep", call.user?.name ?? ""],
    [
      "From",
      call.direction === "inbound" ? (
        <PhoneNumber key="f" e164={call.from} types={call.phone_types} />
      ) : (
        <span className="tnum" key="f">
          {fmtPhone(call.from)}
        </span>
      ),
    ],
    [
      "To",
      call.direction === "outbound" ? (
        <PhoneNumber key="t" e164={call.to} types={call.phone_types} />
      ) : (
        <span className="tnum" key="t">
          {fmtPhone(call.to)}
        </span>
      ),
    ],
    [
      "Hangup",
      call.hangup ? (
        <span key="h" className="font-mono text-caption">
          {call.hangup.cause}
          {call.hangup.sip_code ? `, SIP ${call.hangup.sip_code}` : ""}
          {call.hangup.source ? `, by ${call.hangup.source}` : ""}
        </span>
      ) : (
        ""
      ),
    ],
  ];

  return (
    <>
      <BackLink title={title} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-display font-medium text-display">{title}</h1>
        {call.contact?.company ? <span className="text-body text-ink-2">{call.contact.company}</span> : null}
        <span className="flex items-center gap-1.5">
          <Badge>{call.direction === "outbound" ? "Outbound" : "Inbound"}</Badge>
          {call.disposition ? (
            <Badge>{dispositionName(call.disposition, null, call.disposition_label)}</Badge>
          ) : null}
          {call.dropped ? <Badge tone="danger">Dropped</Badge> : null}
        </span>
        <span className="ml-auto font-mono text-caption text-ink-3" title="Call ID">
          {call.id}
        </span>
      </div>

      <dl className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-surface-1 shadow-material px-4 py-3 sm:grid-cols-3 xl:grid-cols-6">
        {facts.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-caption text-ink-2">{k}</dt>
            <dd className="truncate text-ui">{v || <span className="text-ink-3">None</span>}</dd>
          </div>
        ))}
      </dl>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card title="Call quality">
            <QualityChart samples={call.quality.samples} dropped={call.dropped} />
          </Card>
          <Timeline events={call.events} />
          <Card title="Transcript">
            {call.transcript.length === 0 ? (
              call.processing ? (
                <EmptyState
                  icon={<AudioLines />}
                  title="Transcript processing"
                  description="It appears here when the recording is transcribed."
                />
              ) : (
                <EmptyState
                  title="No transcript"
                  description="Calls that weren't answered have no transcript."
                />
              )
            ) : (
              <>
                {call.processing ? (
                  <p role="status" className="border-b border-hairline px-4 py-2 text-caption text-ink-3">
                    Still processing. The transcript may update when the recording is ready.
                  </p>
                ) : null}
                <ol className="max-h-[420px] overflow-y-auto px-4 py-3">
                  {call.transcript.map((s) => (
                    <li
                      key={`${s.start_ms}-${s.speaker}`}
                      className="grid grid-cols-[52px_84px_1fr] gap-2 py-1.5 text-ui"
                    >
                      <span className="tnum text-ink-3">{fmtDuration(s.start_ms / 1000)}</span>
                      <span
                        className={cx("font-medium", s.speaker === "rep" ? "text-accent-ink" : "text-ink")}
                      >
                        {s.speaker === "rep" ? (call.user?.name.split(" ")[0] ?? "Rep") : "Customer"}
                      </span>
                      <span className={cx(!s.is_final && "text-ink-2 italic")}>{s.text}</span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </Card>
          <CoachingNotes callId={call.id} />
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Card title="Summary">
            {call.summary ? (
              <div className="px-4 py-3 text-ui">
                <p>{call.summary.summary}</p>
                {call.summary.next_steps.length ? (
                  <>
                    <h3 className="mt-3 mb-1 text-caption font-medium text-ink-2">Next steps</h3>
                    <ul className="list-disc space-y-0.5 pl-5">
                      {call.summary.next_steps.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Badge
                    tone={
                      call.summary.sentiment === "positive"
                        ? "ok"
                        : call.summary.sentiment === "negative"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {call.summary.sentiment[0]!.toUpperCase() + call.summary.sentiment.slice(1)}
                  </Badge>
                  {call.summary.follow_up_date ? (
                    <Badge>Follow up {call.summary.follow_up_date}</Badge>
                  ) : null}
                </div>
                <p className="mt-3 text-caption text-ink-3">
                  {call.summary.model}, prompt {call.summary.prompt_version}
                </p>
              </div>
            ) : call.processing ? (
              <EmptyState
                icon={<AudioLines />}
                title="Summary processing"
                description="It's written once the transcript is final."
              />
            ) : (
              <EmptyState title="No summary" />
            )}
          </Card>

          <Card title="CRM delivery" actions={<Badge tone={routing.tone}>{routing.label}</Badge>}>
            <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-2 px-4 py-3 text-ui">
              <dt className="text-ink-2">Mode</dt>
              <dd>{call.routing.mode === "auto" ? "Auto" : "Manual"}</dd>
              <dt className="text-ink-2">Destination</dt>
              <dd>{describeDestination(call.routing.decided)}</dd>
              {call.routing.crm_note_ref ? (
                <>
                  <dt className="text-ink-2">Note</dt>
                  <dd className="font-mono text-caption">
                    {call.routing.crm_note_ref.model} #{call.routing.crm_note_ref.id}
                  </dd>
                </>
              ) : null}
            </dl>
            {call.routing.status === "needs_reconnect" ? (
              <p className="border-t border-hairline px-4 py-2.5 text-ui text-warning">
                Posts automatically once {call.user?.name.split(" ")[0] ?? "the rep"} reconnects Odoo.
              </p>
            ) : null}
          </Card>

          <Card title="Recording">
            {recording === "available" ? (
              <div className="px-4 py-3">
                {/* biome-ignore lint/a11y/useMediaCaption: transcript is shown on the same page */}
                <audio
                  controls
                  preload="none"
                  src={`${API_BASE}/v1/calls/${encodeURIComponent(call.id)}/recording`}
                  className="w-full"
                  aria-label="Call recording"
                />
                <p className="mt-2 text-caption text-ink-2">
                  Playing a recording is recorded in the audit log.
                </p>
              </div>
            ) : recording === "processing" ? (
              <EmptyState
                icon={<AudioLines />}
                title="Recording processing"
                description={
                  call.ended_at
                    ? "It appears here when it's ready, usually within a few minutes of hangup."
                    : "The call is still in progress."
                }
              />
            ) : (
              <EmptyState
                title="No recording"
                description="This call wasn't answered, or recording was off."
              />
            )}
          </Card>
        </div>
      </div>
    </>
  );
}

function Timeline({ events }: { events: CallTimelineEvent[] }) {
  const [source, setSource] = useState<"all" | CallTimelineEvent["source"]>("all");
  const [open, setOpen] = useState<string | null>(null);
  const shown = source === "all" ? events : events.filter((e) => e.source === source);
  return (
    <Card
      title="Timeline"
      actions={
        <Segmented
          label="Event source"
          value={source}
          onChange={setSource}
          options={[
            { value: "all", label: "All" },
            { value: "telnyx", label: "Telnyx" },
            { value: "client", label: "App" },
            { value: "server", label: "Server" },
          ]}
        />
      }
    >
      {shown.length === 0 ? (
        <EmptyState title="No events from this source" />
      ) : (
        <ol className="px-4 py-2">
          {shown.map((e) => (
            <li key={e.id} className="relative border-l border-hairline pb-0.5 pl-4 last:border-transparent">
              <span
                className={cx(
                  "absolute top-[9px] -left-[4.5px] size-2 rounded-full ring-2 ring-surface-1",
                  LEVEL_DOT[e.level],
                )}
              />
              <button
                type="button"
                onClick={() => setOpen(open === e.id ? null : e.id)}
                aria-expanded={e.data ? open === e.id : undefined}
                disabled={!e.data}
                className="grid w-full grid-cols-[92px_64px_minmax(0,180px)_1fr] items-baseline gap-2 rounded-md py-1 text-left text-ui hover:bg-fill-1 disabled:hover:bg-transparent"
              >
                <span className="tnum text-caption text-ink-2">{fmtTime(e.at)}</span>
                <span className="text-caption text-ink-2">{SOURCE_LABEL[e.source]}</span>
                <span
                  className={cx(
                    "truncate font-mono text-caption",
                    e.level === "error" || e.kind === "call.dropped"
                      ? "text-danger-ink"
                      : e.level === "warn"
                        ? "text-warning"
                        : "text-ink",
                  )}
                >
                  {e.kind}
                </span>
                <span className="truncate">{e.message}</span>
              </button>
              {open === e.id && e.data ? (
                <pre className="mb-2 ml-[100px] overflow-x-auto rounded-lg bg-fill-1 p-2.5 font-mono text-micro">
                  {JSON.stringify(e.data, null, 2)}
                </pre>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
